/**
 * useDimensionCooccurrence - Data fetching for dimension co-occurrence
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FilterConfig } from '../../../records/filter';
import type {
  CauseReportDimensions,
  DimensionCooccurrenceData,
  ConductDimensionId,
  UseDimensionCooccurrenceReturn,
} from '../types';
import {
  dimensionsKeys,
  CAUSE_REPORT_STALE_TIME,
} from '../constants';
import { fetchWithAuth } from '../../../../../utils/api';
import { mapFiltersToApiParams, validateDimensions } from '../utils';

interface DimensionsApiResponse {
  dimensions: Array<{
    dimension: string;
    display_name: string;
    description: string;
    record_count: number;
    avg_conduct_score: number;
    top_causes: Array<{
      label_id: string;
      label_name: string;
      count: number;
      percentage: number;
      avg_severity: number;
    }>;
  }>;
  total_records_analyzed: number;
}

function transformDimensions(data: DimensionsApiResponse): CauseReportDimensions {
  const validDimIds = validateDimensions(data.dimensions.map((d) => d.dimension));

  return {
    dimensions: data.dimensions
      .filter((dim) => validDimIds.includes(dim.dimension as ConductDimensionId))
      .map((dim): DimensionCooccurrenceData => ({
        dimension: dim.dimension as ConductDimensionId,
        displayName: dim.display_name,
        description: dim.description,
        recordCount: dim.record_count,
        avgConductScore: dim.avg_conduct_score,
        topCauses: dim.top_causes.map((cause) => ({
          labelId: cause.label_id,
          labelName: cause.label_name,
          count: cause.count,
          percentage: cause.percentage,
          avgSeverity: cause.avg_severity,
        })),
      })),
    totalRecordsAnalyzed: data.total_records_analyzed,
  };
}

async function fetchDimensions(
  datasetId: string,
  params: Record<string, string>
): Promise<DimensionsApiResponse> {
  const queryString = new URLSearchParams(params).toString();

  return fetchWithAuth(
    `/v1/datasets/${datasetId}/cause-report/dimensions?${queryString}`
  );
}

export function useDimensionCooccurrence(
  datasetId: string,
  filters: FilterConfig
): UseDimensionCooccurrenceReturn {
  const filterParams = useMemo(() => mapFiltersToApiParams(filters), [filters]);

  const query = useQuery({
    queryKey: dimensionsKeys.list(datasetId, filterParams),
    queryFn: () => fetchDimensions(datasetId, filterParams),
    select: transformDimensions,
    staleTime: CAUSE_REPORT_STALE_TIME,
    enabled: !!datasetId,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
